/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PreviewType, ScreenType } from 'client-code/previews';
import { HistoryOfferItem } from 'client-code/recordings';

import { DEFAULT_RETRY_OPTIONS } from '../../../../../config';
import { ServicesClientImpl } from '../../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../../core/locale';
import { Log } from '../../../../../core/logging';
import { NetworkManager } from '../../../../../core/network/NetworkManager';
import { RetryMechanism } from '../../../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../../../core/retrymechanism/RetryOptions';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreview } from '../../../../../core/types';
import { setRecordingActivitySelectedRowPreviewAction } from '../../../../../store/settings/recordingactivity/recordingactivity.actions';
import AiringInfo from '../../Preview/AiringInfo';
import DescriptionInfo from '../../Preview/DescriptionInfo';
import PreviewBottom from '../../Preview/PreviewBottom';
import RecordingActivityInfo from '../../Preview/RecordingActivityInfo';
import TopInfo from '../../Preview/TopInfo';
import { getHistoryItemsFromPage, mapHistoryItemToMessageKey } from '../RecordingActivity.utils';

import './RecordingActivityPreview.scss';

const TAG = 'RecordingActivityPreview';
const DEBOUNCE_VALUE = 300;
const services = new ServicesClientImpl();

const RecordingActivityPreview = () => {
    const [recordingPreviewInfo, setRecordingPreviewInfo] = useState<{
        preview: TOfferPreview;
        offerId: string;
    } | null>(null);
    const shouldHideMetadata = services.shouldHideMetadata();
    const dispatch = useTivoDispatch();

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const { verticalPosition, currentPage, recordingItems } = useTivoSelector(
        (state: RootState) => state.recordingActivity,
    );

    const selectedRecordingActivityItem = useMemo(() => {
        const { actualItems } = getHistoryItemsFromPage(recordingItems, currentPage);
        return actualItems[verticalPosition] as HistoryOfferItem;
    }, [currentPage, recordingItems, verticalPosition]);

    const fetchRecordingPreviewInfo = useCallback(async (recordingId: string) => {
        if (!recordingId) return;
        try {
            const fetchOfferPreview = services.fetchRecordingPreview.bind(services);
            const preview = RetryMechanism.callServiceWithRetry<TOfferPreview>(
                fetchOfferPreview,
                [{ recordingId, applicationFeatureArea: 'recordingActivity' }, ScreenType.All],
                DEFAULT_RETRY_OPTIONS as RetryOptions,
            );
            dispatch(setRecordingActivitySelectedRowPreviewAction(preview));
            preview.then((data) => {
                if (data) {
                    setRecordingPreviewInfo({ preview: data, offerId: recordingId });
                }
            });
        } catch (error) {
            Log.e(TAG, `Failed to fetch preview: ${error}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setRecordingPreviewInfo(null);
        dispatch(setRecordingActivitySelectedRowPreviewAction(null));
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            if (selectedRecordingActivityItem?.recordingId) {
                fetchRecordingPreviewInfo(selectedRecordingActivityItem.recordingId);
            }
        }, DEBOUNCE_VALUE);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRecordingActivityItem?.recordingId, fetchRecordingPreviewInfo]);

    if (
        !recordingPreviewInfo ||
        !selectedRecordingActivityItem ||
        NetworkManager.isNetworkDisconnected()
    )
        return null;

    const { offerPreview } = recordingPreviewInfo.preview;
    if (!offerPreview) return null;

    const { contentPreview } = offerPreview;

    if (!contentPreview) return null;

    if (recordingPreviewInfo.offerId !== selectedRecordingActivityItem.recordingId) {
        return null;
    }

    const {
        startTime: startTimeStr,
        endTime: endTimeStr,
        description,
        isAdult,
        movieYear,
        previewType,
        channel,
        seasonNumber,
        episodeNumber,
        episodeTitle,
        title,
    } = contentPreview;

    const startTime = startTimeStr ? Date.parse(startTimeStr) : 0;
    const endTime = endTimeStr ? Date.parse(endTimeStr) : 0;
    const videoResolution = offerPreview?.videoResolution?.toLowerCase();
    const isMovie = previewType === PreviewType.MoviePreview;
    const hideMetadata = shouldHideMetadata ? !!isAdult : false;
    const { preview } = mapHistoryItemToMessageKey(selectedRecordingActivityItem);

    return (
        <div className="recording-activity-preview" data-testid="recording-activity-preview">
            <AiringInfo
                startTime={startTime}
                endTime={endTime}
                channelNumber={channel?.channelNumber}
                callSign={channel?.callSign}
                videoResolution={videoResolution}
            />
            <RecordingActivityInfo preview={preview} />
            <TopInfo
                title={title}
                episodeNumber={episodeNumber}
                episodeTitle={episodeTitle}
                episodeSeason={seasonNumber}
                isMovie={isMovie}
                movieYear={movieYear}
                hideMetadata={hideMetadata}
            />
            <DescriptionInfo
                description={
                    hideMetadata
                        ? Locale.getString(LocaleKeys.PREVIEW_HIDDEN_DESCRIPTION)
                        : description
                }
            />
            <PreviewBottom contentPreview={contentPreview} />
        </div>
    );
};

export default RecordingActivityPreview;
